import get from "lodash/get";
import { createSelector } from "reselect";
import { compactJoin } from "utils/helpers";
import isEmpty from "lodash/isEmpty";
import has from "lodash/has";
import isArray from "lodash/isArray";
import { DATE_STYLES, formatDate } from "utils/i18nFormatter";
import moment from "moment";

import { DEFAULT_REGEX, forms } from "utils/constants";

const userDetailsDomain = state =>
  get(state, "api.loggedInUserDetails.data", {});
const userDetailsApiDomain = state => get(state, "api.loggedInUserDetails");
const userProfileDomain = state => get(state, "api.userProfileDetails", {});
const newConfigProfileParams = state =>
  get(state, "api.newProfileParameters.data", []);
const analyticsProfileDomain = state =>
  get(state, "api.analyticPreferences.data", {});

const defaultAnalyticsProfileDomain = state =>
  get(state, "api.defaultAnalytics.data", {});

const enabledPaymentMethodsForUser = state =>
  get(state, "api.enabledPaymentMethods.data", {});

const paymentMethodIdData = state =>
  get(state, "api.paymentMethodIdToEditOrRemove", null);

const PaymentMethodIdToToggle = state =>
  get(state, "api.PaymentMethodIdToToggleDefault", null);

const getDataForAddPaymentMethod = state =>
  get(state, "api.getDataForAddPaymentMethod.data", {});

const getIntentForAddStripeCard = state =>
  get(state, "api.getIntentForAddStripeCard.data", {});

const profileFormDomain = state =>
  get(state, `form.${forms.PROFILE_FORM}.values`, {});

const unsubscribeApiDomain = state =>
  get(state, "api.unsubscribe.data", {});

export const currentPhoneNumberSelector = createSelector(
  userDetailsDomain,
  userDetails => get(userDetails, "telco_info[0].phone_no")
);

export const loggedInUserDetailsSelector = createSelector(
  userDetailsApiDomain,
  userProfileApi => get(userProfileApi, "data", {})
);

export const loggedInUserDetailsMultiLineSelector = createSelector(
  userDetailsApiDomain,
  profileFormDomain,
  (userProfileApi, activeLineData) => {
    let telcoInfo = get(userProfileApi, "data", {});
    let selectedActiveLine = get(activeLineData, "selected_active_line", {});
    if (!isEmpty(selectedActiveLine)) {
      telcoInfo.telco_info = selectedActiveLine?.value;
    }
    return telcoInfo;
  }
);

export const activeLineNumberSelector = createSelector(
  userDetailsApiDomain,
  userProfileApi => {
    const activeLineInfo = get(userProfileApi?.data, "telco_info", []);
    return activeLineInfo.map(activeNumber => ({
      label: activeNumber?.phone_no,
      value: activeNumber
    }));
  }
);

export const analyticDetailsSelector = createSelector(
  analyticsProfileDomain,
  analyticsData => get(analyticsData, "enabled", true)
);

export const defaultAnalyticDetailsSelector = createSelector(
  defaultAnalyticsProfileDomain,
  defaultAnalyticsData =>
    get(defaultAnalyticsData, "analyticDefaultPref.web", true)
);

export const enabledPaymentMethodsListSelector = createSelector(
  enabledPaymentMethodsForUser,
  enabledPaymentMethodsData =>
    get(enabledPaymentMethodsData, "paymentMethods", [])
);

export const paymentMethodToEditOrRemoveSelector = createSelector(
  paymentMethodIdData,
  paymentMethodId => get(paymentMethodId, "data", null)
);

export const paymentMethodToToggleSelector = createSelector(
  PaymentMethodIdToToggle,
  PaymentMethodIdToToggleDefault =>
    get(PaymentMethodIdToToggleDefault, "data", null)
);

export const getDataForAddPaymentMethodSelector = createSelector(
  getDataForAddPaymentMethod,
  AddPaymentMethodData => AddPaymentMethodData
);

export const getIntentForAddCardSelector = createSelector(
  getIntentForAddStripeCard,
  addCardIntentData => addCardIntentData
);

export const unsubscribeInfoSelector = createSelector(
  unsubscribeApiDomain,
  unsubscribeInfo => unsubscribeInfo
);

/**
 * Returns the user profile details
 */
export const userProfileDetailsSelector = createSelector(
  userProfileDomain,
  userDetailsDomain,
  (profileDetails, userDetails) => {
    const first_name = get(userDetails, "first_name");
    const middle_name = get(userDetails, "middle_name");
    const last_name = get(userDetails, "last_name");
    const fullName = compactJoin([first_name, middle_name, last_name], " ");
    const dob = get(profileDetails, "data.userDOB", "")
      ?.split("-")
      .join("/");
    const userDOB = dob
      ? formatDate(moment(dob, "DD/MM/YYYY"), {
          style: DATE_STYLES.SHORT
        })
      : undefined;
    return isEmpty(profileDetails)
      ? {}
      : {
          ...profileDetails.data,
          fullName,
          ...(userDOB && { userDOB: userDOB })
        };
  }
);

export const newConfigProfileNameValidationSelector = createSelector(
  newConfigProfileParams,
  addprofileParams =>
    addprofileParams[0]?.groupName === "name" ? addprofileParams[0] : {}
);

export const personalContacNumberRegEx = createSelector(
  newConfigProfileParams,
  addprofileParams => {
    const personalContactParams = addprofileParams.find(
      personalContactParam => personalContactParam.groupName === "contactNumber"
    );
    return (
      get(personalContactParams, "attributes[0].regex") ||
      DEFAULT_REGEX.PERSONAL_CONTACT_NUMBER
    );
  }
);

const makeTemplate = (
  name,
  displayName,
  enabled,
  regex,
  mandatory,
  kycRequired,
  disablingAllowed,
  disablingMandatory,
  type
) => {
  return {
    attribute: name,
    displayTitle: displayName,
    enabled,
    regex,
    required: mandatory,
    kycRequired,
    disablingAllowed,
    disablingMandatory,
    type
  };
};
const createOldProfileObject = getProfileParams => {
  const Template = [];
  isArray(getProfileParams) &&
    getProfileParams.forEach(outerItem => {
      has(outerItem, "attributes") &&
        outerItem.attributes.map(item =>
          Template.push(
            makeTemplate(
              item.name,
              item.displayName,
              item.enabled,
              item.regex,
              item.mandatory,
              outerItem.kycRequired,
              item.disablingAllowed,
              item.disablingMandatory,
              item.type
            )
          )
        );
    });
  return Template;
};

export const newConfigprofileValidationSelector = createSelector(
  newConfigProfileParams,
  addprofileParams => {
    const profileValidation = createOldProfileObject(addprofileParams); //get(addprofileParams, "profile_validation", []) ?? [];
    const validations = profileValidation.reduce(
      (o, key) => ({
        [key?.attribute.replace(".", "_")]: { ...key },
        ...o
      }),
      {}
    );
    return validations;
  }
);

export const makeSelectGetAddCardIntentData = () => getIntentForAddCardSelector;

export const makeSelectGetDataForAddPaymentMethod = () =>
  getDataForAddPaymentMethodSelector;

export const makeSelectEnabledPaymentListMethods = () =>
  enabledPaymentMethodsListSelector;
export const makeSelectCurrentPhoneNumber = () => currentPhoneNumberSelector;
export const makeSelectLoggedInUserDetails = () => loggedInUserDetailsSelector;
export const makeSelectActiveLine = () => activeLineNumberSelector;
export const makeSelectUserProfileDetails = () => userProfileDetailsSelector;
export const makeSelectAnalyticSettings = () => analyticDetailsSelector;
export const makeSelectPersonalContacNumberRegEx = () =>
  personalContacNumberRegEx;
export const makeSelectDefaultAnalyticSettings = () =>
  defaultAnalyticDetailsSelector;
export const makeSelectPaymentMethodToEditOrRemove = () =>
  paymentMethodToEditOrRemoveSelector;
export const makeSelectPaymentMethodToToggleDefault = () =>
  paymentMethodToToggleSelector;
export const makeSelectProfileConfigValidations = () =>
  newConfigprofileValidationSelector;
export const makeSelectUnsubscribeInfo = () =>
  unsubscribeInfoSelector;
